import { Event, EventRecordEventTypes } from '@eagle/data-function-types';
import { Accordion, AccordionDetails, AccordionSummary, Button, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { MenuList, useTheme } from '@mui/material';
import { useEffect, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { getEventLabel, getEventLabelV2 } from '../../util';
import { useBoolFlag } from '../flags';
import { CircleIcon, Icon } from '../icons';
import { EventInstance } from './event-instance';
import { getIconStyle } from './event.util';

interface Props<T> {
  events: T[];
  eventTypeId: string;
  instanceExpanded?: boolean;
  renderEventGroupDialog?: (
    closeDialog: () => void,
    eventIndex: number,
    events: T[],
    open: boolean,
    setEventIndex: (index: number) => void,
  ) => JSX.Element;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  paddingTop: `${theme.spacing(2)}`,
  '&:before': { display: 'none' },
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  '& .MuiAccordionSummary-content': {
    justifyContent: 'space-between',
    margin: 0,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transform: 'rotate(-90deg)',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(0deg)',
  },
  flexDirection: 'row-reverse',
});

const THING_EVENTS_INITIAL_DISPLAY = 10;

/**
  * NATIVE FUNCTION: Component for event group
  */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function EventGroup<T extends Omit<Event, 'schema'>>({ events, eventTypeId, renderEventGroupDialog, instanceExpanded }: Props<T>): JSX.Element {
  const { t } = useTranslation(['common']);
  const [eventIndex, setEventIndex] = useState(0);
  const [isEventDialogOpen, setIsEventDialogOpen] = useState(false);
  const theme = useTheme();
  const { feature, featureTypeId } = events[0];
  const iconStyle = getIconStyle(theme, eventTypeId, feature, featureTypeId);
  const [showAll, setShowAll] = useState(false);
  const flagUseEventV3 = useBoolFlag('portals-translation-retrieval-hook-for-event-and-alert-descriptions-feature');
  const displayText = flagUseEventV3 ? getEventLabel(events[0].feature, events[0].eventTypeId) : eventTypeId === EventRecordEventTypes.OCCURRENCE
    ? getEventLabelV2(events[0].featureTypeId, eventTypeId, feature)
    : t('common:event-description', { featureTypeId: events[0].featureTypeId, eventTypeId });

  const openDialog = (index: number): void => {
    if (!events) return;
    setEventIndex(index);
    setIsEventDialogOpen(true);
  };

  const closeDialog = (): void => setIsEventDialogOpen(false);

  const handleClick = (index: number): void => {
    if (!renderEventGroupDialog) return;
    openDialog(index);
  };

  useEffect(() => {
    !instanceExpanded && setShowAll(false);
  }, [instanceExpanded]);

  return <>
    <StyledAccordion onChange={(_, expanded) => !expanded && setShowAll(false)}>
      <StyledAccordionSummary expandIcon={<Icon icon="ChevronDown" />}>
        <Stack direction="row" spacing={2} alignItems="center" pl={2}>
          <CircleIcon sx={iconStyle} />
          <Stack direction="column">
            <Typography color="text.secondary" fontSize="small">
              {t('common:component.events.hint.eventsWithCount', { count: events.length })}
            </Typography>
            <Typography>{displayText}</Typography>
          </Stack>
        </Stack>
      </StyledAccordionSummary>
      <AccordionDetails>
        <Stack spacing={1} pl={2}>
          <MenuList dense>
            {events.slice(0, showAll ? events.length : THING_EVENTS_INITIAL_DISPLAY).map((event, i) => <EventInstance<T> key={i} event={event} onClick={() => handleClick(i)} />)}
          </MenuList>
          <Button variant="text" onClick={() => setShowAll(true)} data-testid="action-button-events-show-all">
            {t('common:common.action.show-all')}
          </Button>
        </Stack>
      </AccordionDetails>
    </StyledAccordion>
    {renderEventGroupDialog?.(closeDialog, eventIndex, events, isEventDialogOpen, setEventIndex)}
  </>;
}
